import { useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { Heading, Grid, Alert, AlertIcon, Spinner, Flex } from "@chakra-ui/react";
import { useGetTransactionMethods } from "api/transaction-methods";
import AddBankAccountButton from "./AddBankAccountButton";
import BankAccount from "components/PaymentMethodSelector/BankAccount";

const BankAccountList = ({ description, onCountryChange, onBankAccountClick }) => {
  const location = useLocation();
  const history = useHistory();

  const {
    data: transactionMethodsData,
    isLoading: isLoadingGetTransactionMethods,
    isError: isGetTransactionMethodsError,
    refetch: refetchTransactionMethods,
  } = useGetTransactionMethods({ payout_options: true, bank_accounts: true }); // TODO: really shouldn't need the 'payout_options' here, but coming across a caching issue in SelectPayoutMethod component so adding this for now

  /**
   * useEffect to handle a new bank account having been added
   * and us immediately taking the user to the Masspay options view.
   */
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const newlyAddedBankAccountId = queryParams.get("newlyAddedBankAccountId");
    const [bankAccount] = Object.values(
      transactionMethodsData?.payload?.bank_accounts || {},
    ).filter((v) => v.bank_account_id === newlyAddedBankAccountId);
    if (newlyAddedBankAccountId && bankAccount && onBankAccountClick) {
      queryParams.delete("newlyAddedBankAccountId");
      history.replace({
        search: queryParams.toString(),
      });
      onBankAccountClick(bankAccount);
    }
  }, [location, history, transactionMethodsData, onBankAccountClick]);

  let bankAccountsContent;
  if (isLoadingGetTransactionMethods) {
    bankAccountsContent = (
      <Flex justify="center">
        <Spinner 
        speed="0.5s"
        emptyColor="gray.200"
        color="integrator.500"
        data-testid="transaction-methods-loading-spinner"
      />
      </Flex>
    );
  } else if (isGetTransactionMethodsError) {
    // unlikely we get here since we would have loaded transaction methods before this is viewed, but just in case...
    bankAccountsContent = (
      <Alert status='error'>
        <AlertIcon />
        <FormattedMessage defaultMessage="There was an error retrieving bank account details" description="Section header" />
      </Alert>
    );
  } else {
    const bankAccounts = Object.values(transactionMethodsData.payload?.bank_accounts || {});
    bankAccountsContent = (
      <>
        {bankAccounts.map((bankAccount) => {
          const linkedPMs = Object.values(
            transactionMethodsData.payload?.payment_methods || {},
          ).filter((v) => v.provider_data?.bank_account_id === bankAccount.bank_account_id);
          return (
            <BankAccount
              key={`bank-account-${bankAccount.bank_account_id}`}
              bankAccount={bankAccount}
              linkedPaymentMethods={linkedPMs}
              onBankAccountClick={() => {
                onBankAccountClick(bankAccount);
              }}
              onUpdate={refetchTransactionMethods}
            />
          );
        })}
      </>
    );
  }

  return (
    <Grid gridGap="4">
      <Heading as="h5" size="sm" color="secondaryText">
        <FormattedMessage defaultMessage="Linked Bank Accounts" description="Section header" />
      </Heading>
      {bankAccountsContent}
      <AddBankAccountButton
        onSelect={onCountryChange}
        description={description}
        countrySelectSize="xs"
      />
    </Grid>
  );
};

export default BankAccountList;
